"use memo"

import {
    useInvalidateUseGetAutopilotTasksForCampaign,
    useWithdrawWorkflow,
} from '@urbanx/agx.activities.client'
import type { WorkflowTaskRequest } from '@urbanx/agx.activities.client'
import { AuthContext } from '@urbanx/agx.auth.client'
import { DownloadDocumentTypes } from '@urbanx/agx.campaigns.client'
import type { UploadedDocument } from '@urbanx/agx.campaigns.client'
import type { RadioGroupOptions } from '@urbanx/agx.ui-components2'
import type { AxiosProgressEvent, GenericAbortSignal } from 'axios'
import { useSelectedCampaign } from 'hooks'
import { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { uploadFile } from 'services'

// NOTE: remove this token once the backend is ready 
// https://urbanx-development.atlassian.net/browse/AG-6844
const FEATURE_TERMINATE_CONTRACT = false

const DEPOSIT_OPTIONS: RadioGroupOptions[] = [
    { label: 'Buyer', value: 'Buyer' },
    { label: 'Vendor', value: 'Vendor' },
    { label: 'No deposit received', value: 'noDepositReceived' },
]

const REASON_OPTIONS: RadioGroupOptions[] = [
    { label: 'Cooing off period', value: 'coolingOffPeriod' },
    { label: 'Failed Finance', value: 'failedFinance' },
    { label: 'Failed Building & Pest', value: 'buildingPest' },
    { label: 'Special Conditions', value: 'specialConditions' },
    { label: 'Other', value: 'other' },
]

interface UseContractTerminateProps {
    isOpen: boolean
    setIsOpen: (_value: boolean) => void
    onContractTerminated?: () => void
}

export const useContractTerminate = (props: UseContractTerminateProps) => {
    const { onContractTerminated, isOpen, setIsOpen } = props
    const { campaign: selectedCampaign } = useSelectedCampaign()
    const { contractId } = useParams()
    const [submitting, setIsSubmitting] = useState(false)
    const { getAuthToken } = useContext(AuthContext)
    const [selectedReason, setSelectedReason] = useState<string | undefined>(
        undefined
    )
    const [selectedDepositOption, setSelectedDepositOption] = useState<
        string | undefined
    >(undefined)
    const [selectedTerminationDate, setSelectedTerminationDate] =
        useState<Date | null>(null)
    const [vendorTerminationConfirmationFiles, setVendorTerminationConfirmationFiles] =
        useState<(UploadedDocument | File)[]>([])
    const [buyerTerminationConfirmationFiles, setBuyerTerminationConfirmationFiles] =
        useState<(UploadedDocument | File)[]>([])
    const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()

    const { withdrawWorkflow } = useWithdrawWorkflow({
        onSuccess: () => {
            invalidateTasks(selectedCampaign?.campaignId ?? '', contractId)
        },
        onError: () => { },
    })

    const buildRenamedFormData = (formData: FormData) => {
        const file = formData.get('File')
        if (!file || typeof File === 'undefined' || !(file instanceof File)) {
            return formData
        }

        const renamedFile = new File([file], `${contractId}/${file.name}`, {
            type: file.type,
            lastModified: file.lastModified,
        })
        const renamedFormData = new FormData()
        renamedFormData.append('File', renamedFile)
        return renamedFormData
    }

    const onUploadFile = async (
        authToken: string,
        file: string | FormData,
        uploadCampaignId: string,
        uploadedFormType: DownloadDocumentTypes,
        uploadProgress?: (progressEvent: AxiosProgressEvent) => void,
        abortSignal?: GenericAbortSignal,
        uploadContractId?: string
    ) => {
        try {
            const renamedFormData =
                typeof FormData !== 'undefined' && file instanceof FormData
                    ? buildRenamedFormData(file)
                    : file

            return await uploadFile(
                authToken,
                renamedFormData,
                uploadCampaignId,
                uploadedFormType,
                uploadProgress ?? null,
                abortSignal ?? null,
                undefined,
                uploadContractId
            )
        } catch {
            return undefined
        }
    }

    const uploadSelectedFile = async (
        authToken: string,
        uploadCampaignId: string,
        documentType: DownloadDocumentTypes,
        selectedFile?: UploadedDocument | File,
        uploadContractId?: string
    ) => {
        if (!selectedFile || !(selectedFile instanceof File)) return selectedFile

        const formData = new FormData()
        formData.append('File', selectedFile)

        return onUploadFile(
            authToken,
            formData,
            uploadCampaignId,
            documentType,
            undefined,
            undefined,
            uploadContractId
        )
    }

    const onTerminateConfirm = async () => {
        const request: WorkflowTaskRequest = {
            campaignId: selectedCampaign?.campaignId,
            contractId,
        }
        withdrawWorkflow(request)

        // NOTE: remove this check once the backend is ready, Also will have to update the documentType
        // https://urbanx-development.atlassian.net/browse/AG-6844
        if (FEATURE_TERMINATE_CONTRACT) {
            const authToken = await getAuthToken()
            if (
                !selectedCampaign?.campaignId ||
                vendorTerminationConfirmationFiles.length === 0 ||
                buyerTerminationConfirmationFiles.length === 0
            ) {
                return
            }
            if (!authToken) return

            try {
                await uploadSelectedFile(
                    authToken,
                    selectedCampaign.campaignId,
                    DownloadDocumentTypes.ExchangeLetter,
                    vendorTerminationConfirmationFiles[0],
                    contractId
                )

                await uploadSelectedFile(
                    authToken,
                    selectedCampaign.campaignId,
                    DownloadDocumentTypes.SignedContract,
                    buyerTerminationConfirmationFiles[0],
                    contractId
                )

                setIsOpen(false)
            } finally {
                setIsSubmitting(false)
            }
        } else {
            setIsOpen(false)
        }
    }

    const onTerminateSuccess = () => {
        onContractTerminated?.()
    }

    const onDateChange = (date: Date | null) => {
        setSelectedTerminationDate(date)
    }

    const onVendorTerminationConfirmationChanged = ({
        value,
    }: {
        value?: (UploadedDocument | File)[]
    }) => {
        setVendorTerminationConfirmationFiles(value ?? [])
    }

    const onBuyerTerminationConfirmationChanged = ({
        value,
    }: {
        value?: (UploadedDocument | File)[]
    }) => {
        setBuyerTerminationConfirmationFiles(value ?? [])
    }

    const onDepositOptionChange = (value: string) => {
        setSelectedDepositOption(value)
    }

    const onReasonChange = (value: string) => {
        setSelectedReason(value)
    }

    const canSubmit =
        selectedTerminationDate != null &&
        vendorTerminationConfirmationFiles.length > 0 &&
        buyerTerminationConfirmationFiles.length > 0 &&
        selectedReason != null &&
        selectedDepositOption != null &&
        !submitting

    useEffect(() => {
        if (isOpen) return

        setSelectedDepositOption(undefined)
        setSelectedReason(undefined)
        setSelectedTerminationDate(null)
        setVendorTerminationConfirmationFiles([])
        setBuyerTerminationConfirmationFiles([])
        setIsSubmitting(false)
    }, [isOpen])

    return {
        onTerminateSuccess,
        onTerminateConfirm,
        depositOptions: DEPOSIT_OPTIONS,
        reasonOptions: REASON_OPTIONS,
        onDateChange,
        canSubmit,
        vendorTerminationConfirmationFiles,
        buyerTerminationConfirmationFiles,
        onVendorTerminationConfirmationChanged,
        onBuyerTerminationConfirmationChanged,
        onDepositOptionChange,
        onReasonChange,
        onUploadFile,
        getAuthToken,
        selectedCampaign,
    }
}
